export type LocationViewMode = "list" | "map";

/** Search param for location-management map vs list layout. */
export const LOCATION_VIEW_MODE_PARAM = "view";

/** localStorage key for the last view mode this viewer picked. */
export const LOCATION_VIEW_MODE_STORAGE_KEY = "shadow-realm:location-view-mode";

export const DEFAULT_LOCATION_VIEW_MODE: LocationViewMode = "map";

const VIEW_MODES = new Set<string>(["list", "map"]);

export const isLocationViewMode = (value: unknown): value is LocationViewMode =>
	typeof value === "string" && VIEW_MODES.has(value);

/** Read view mode from the current URL search params. */
export const parseLocationViewMode = (
	searchParams: URLSearchParams,
): LocationViewMode | null => {
	const raw = searchParams.get(LOCATION_VIEW_MODE_PARAM);
	return isLocationViewMode(raw) ? raw : null;
};

/** Read the stored view-mode preference (or null if unset/unavailable). */
export const readStoredLocationViewMode = (): LocationViewMode | null => {
	try {
		const raw = localStorage.getItem(LOCATION_VIEW_MODE_STORAGE_KEY);
		return isLocationViewMode(raw) ? raw : null;
	} catch {
		// Private browsing or blocked site data.
		return null;
	}
};

/** Persist the view-mode preference for future visits. */
export const storeLocationViewMode = (mode: LocationViewMode): void => {
	try {
		localStorage.setItem(LOCATION_VIEW_MODE_STORAGE_KEY, mode);
	} catch {
		// Persistence is a convenience; in-memory / URL still work.
	}
};

/**
 * Resolve the active view mode: URL wins, then localStorage, then map.
 */
export const resolveLocationViewMode = (
	searchParams: URLSearchParams,
): LocationViewMode =>
	parseLocationViewMode(searchParams) ??
	readStoredLocationViewMode() ??
	DEFAULT_LOCATION_VIEW_MODE;

/**
 * Apply a view mode on a copy of the current search params.
 * Preserves unrelated params (e.g. edit targets).
 */
export const applyLocationViewMode = (
	current: URLSearchParams,
	mode: LocationViewMode,
): URLSearchParams => {
	const next = new URLSearchParams(current);
	next.set(LOCATION_VIEW_MODE_PARAM, mode);
	return next;
};
